import { useCallBlockV1QueryUpdaters } from '../query-updaters/use-query-updaters';
import { CreateBlockedNumberIO } from '../types';
import { UseCallBlockV1MutationEndpointArgs, useCallBlockV1Mutation } from './use-call-block-v1-mutation';

type MutationContext<C = unknown> = {
  tempId?: string;
  otherContext?: C;
};

/**
 * A hook that returns a mutation for the `CreateBlockedNumber` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 */
export const useCreateBlockedNumberMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends CreateBlockedNumberIO['input'] = CreateBlockedNumberIO['input']
>({
  options,
  optimisticUpdate = false,
  ...args
}: UseCallBlockV1MutationEndpointArgs<'CreateBlockedNumber', E, C | undefined, OtherOptions, RequestOverride> = {}) => {
  const {
    cancelBlockedNumbersQueries,
    addBlockedNumber,
    removeBlockedNumber,
    replaceBlockedNumber,
    invalidateBlockedNumbers,
  } = useCallBlockV1QueryUpdaters();

  return useCallBlockV1Mutation<'CreateBlockedNumber', E, MutationContext<C>, OtherOptions, RequestOverride>({
    endpointName: 'CreateBlockedNumber',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => {
        let tempId: string | undefined;
        if (optimisticUpdate) {
          tempId = `optimistic-${Date.now()}`;
          await cancelBlockedNumbersQueries(request.tenantId);
          addBlockedNumber(request.tenantId, {
            id: tempId,
            tenantId: request.tenantId,
            phoneNumber: request.phoneNumber,
            createdAt: new Date().toISOString(),
          });
        }

        return {
          tempId,
          otherContext: await options?.onMutate?.(request),
        };
      },
      onSuccess: (response, request, context) => {
        if (context?.tempId && response.blockedNumber) {
          replaceBlockedNumber(request.tenantId, context.tempId, response.blockedNumber);
        }

        return options?.onSuccess?.(response, request, context?.otherContext);
      },
      onError: (error, request, context) => {
        if (context?.tempId) {
          removeBlockedNumber(request.tenantId, context.tempId);
        }

        return options?.onError?.(error, request, context?.otherContext);
      },
      onSettled: (response, error, request, context) => {
        invalidateBlockedNumbers(request.tenantId);

        // Only pass context of type C into provided `onSettled` option
        return options?.onSettled?.(response, error, request, context?.otherContext);
      },
    },
  });
};
